import { useTranslation } from 'react-i18next'

import type { ActivityChannel, ActivityStatus } from '@/admin/hooks/use-communications-activity'
import { cn } from '@/shared/lib/utils'

/**
 * Shared pills for the communications activity surfaces.
 *
 * Shape and tokens match the `/settings/api-logs` log-pill idiom (mono, 3px
 * radius, tinted from the status tokens) so the activity table reads as part of
 * the same family rather than a new one.
 *
 * Two colour axes, and they do not compete: channel is a hue (which pipe it went
 * down) while status is a semantic tone (what happened to it). Channel sits in a
 * fixed-width column at a constant position, so hue reads as a category rather
 * than as a signal.
 */

const PILL_BASE =
  'inline-flex items-center justify-center rounded-[3px] px-[7px] py-[2px] font-mono text-[10.5px] font-medium tracking-[0.02em]'

type Tone = 'ok' | 'warn' | 'error' | 'transit' | 'mute'

const TONE_CLASS: Record<Tone, string> = {
  ok: 'bg-[var(--status-ok-bg)] text-[var(--status-ok)]',
  warn: 'bg-[var(--status-warn-bg)] text-[var(--status-warn)]',
  error: 'bg-[var(--status-error-bg)] text-[var(--status-error)]',
  transit: 'bg-[var(--status-in-progress-bg)] text-[var(--status-in-progress)]',
  mute: 'bg-[var(--bg-subtle)] text-[var(--ink-2)]',
}

/** One hue per channel. Falls back to neutral for a channel added later. */
const CHANNEL_CLASS: Record<ActivityChannel, string> = {
  email: 'bg-[var(--channel-email-bg)] text-[var(--channel-email)]',
  sms: 'bg-[var(--channel-sms-bg)] text-[var(--channel-sms)]',
  whatsapp: 'bg-[var(--channel-whatsapp-bg)] text-[var(--channel-whatsapp)]',
  voice: 'bg-[var(--channel-voice-bg)] text-[var(--channel-voice)]',
  mail: 'bg-[var(--channel-mail-bg)] text-[var(--channel-mail)]',
}

const STATUS_TONE: Record<ActivityStatus, Tone> = {
  queued: 'mute',
  // Handed to the provider, not yet confirmed delivered. Deliberately not green:
  // most tenants have no delivery webhook, and painting "sent" as success would
  // overstate deliverability - the one thing this page exists to report honestly.
  sent: 'transit',
  delivered: 'ok',
  opened: 'ok',
  clicked: 'ok',
  bounced: 'error',
  failed: 'error',
  // Not an error: a skip is often configuration working as authored. The reason
  // is what matters, and it renders beside this.
  skipped: 'warn',
}

const STATUS_DEFAULTS: Record<ActivityStatus, string> = {
  queued: 'Queued',
  sent: 'Sent',
  delivered: 'Delivered',
  opened: 'Opened',
  clicked: 'Clicked',
  bounced: 'Bounced',
  failed: 'Failed',
  skipped: 'Not sent',
}

const CHANNEL_DEFAULTS: Record<ActivityChannel, string> = {
  email: 'Email',
  sms: 'SMS',
  whatsapp: 'WhatsApp',
  voice: 'Voice',
  mail: 'Mail',
}

export function ActivityStatusPill({
  status,
  className,
}: {
  status: ActivityStatus
  className?: string
}) {
  const { t } = useTranslation()
  return (
    <span
      className={cn(PILL_BASE, 'min-w-[64px]', TONE_CLASS[STATUS_TONE[status] ?? 'mute'], className)}
    >
      {t(`comms.status.${status}`, { defaultValue: STATUS_DEFAULTS[status] ?? status })}
    </span>
  )
}

export function ActivityChannelPill({
  channel,
  className,
}: {
  channel: ActivityChannel
  className?: string
}) {
  const { t } = useTranslation()
  return (
    <span
      data-channel={channel}
      className={cn(
        PILL_BASE,
        'min-w-[58px] uppercase',
        CHANNEL_CLASS[channel] ?? TONE_CLASS.mute,
        className,
      )}
    >
      {t(`comms.channel.${channel}`, { defaultValue: CHANNEL_DEFAULTS[channel] ?? channel })}
    </span>
  )
}

/**
 * Why a message did not send.
 *
 * Renders the server-supplied label only. The machine code never reaches the
 * screen - see docs/communications-transparency.md §5.2.
 */
export function ActivitySkipReason({
  label,
  className,
}: {
  label: string
  className?: string
}) {
  if (!label) return null
  return <span className={cn(PILL_BASE, TONE_CLASS.warn, className)}>{label}</span>
}
